// src/utils/layout/buildDocumentDefs.js
//
// Floor plan ve site plan'dan dokuman tanimlari listesi olusturma.

import { INITIAL_DEFS, DOCUMENT_OFFSET } from './layoutConstants';
import { hideOutlierMeshes } from './layoutHelpers';
import { PROJECT_TYPE_DOCUMENTS, DOCUMENT_TYPES, PROJECT_TYPES } from '../../../constants/projectTypes';
import { groupPanelsByContent } from './groupPanelsByContent';

// INITIAL_DEFS ve buildDocumentDefs i\u00e7indeki "T\u00fcrk\u00e7e display name"ler ile
// PROJECT_TYPE_DOCUMENTS'taki snake_case key'ler aras\u0131nda k\u00f6pr\u00fc.
// (Mevcut isimler bozulmadan filtre eklemek i\u00e7in.) Bu tabloda olmayan
// isimler her tipte izinlidir (\u00f6rn. dinamik "Kat Plan\u0131", "Ground Floor").
const DEF_NAME_TO_DOC_KEY = {
  'Topraklama Hesab\u0131': DOCUMENT_TYPES.TOPRAKLAMA_HESAP,
  'Topraklama Detaylar\u0131': DOCUMENT_TYPES.TOPRAKLAMA_DETAY,
  'Kablo Bacas\u0131': DOCUMENT_TYPES.KABLO_BACASI,
  'Gerilim D\u00fc\u015f\u00fcm\u00fc': DOCUMENT_TYPES.GERILIM_DUSUM,
  'Pano Detay': DOCUMENT_TYPES.PANO_DETAY,
  'Enerji Odasi': DOCUMENT_TYPES.ENERJI_ODASI,
  'Sembol Listesi': DOCUMENT_TYPES.SEMBOLLER,
  'Notlar': DOCUMENT_TYPES.NOTLAR,
  'Baslik': DOCUMENT_TYPES.BASLIK,
  // buildDocumentDefs i\u00e7inde ayr\u0131ca \u00fcretilenler
  'Y\u00fckleme Cetveli': DOCUMENT_TYPES.YUKLEME_CETVELI,
  'Kolon \u015eemas\u0131': DOCUMENT_TYPES.KOLON_SEMASI,
  'TekHat  \u015eemas\u0131': DOCUMENT_TYPES.TEK_HAT_SEMASI,
  'Vaziyet Plan\u0131': DOCUMENT_TYPES.VAZIYET_PLANI,
};

export function buildDocumentDefs(floorPlans, sitePlan, MAIN_FRAME_HEIGHT, projectType, simpleSupply) {
  const allowedKeys = projectType && PROJECT_TYPE_DOCUMENTS[projectType]
    ? new Set(PROJECT_TYPE_DOCUMENTS[projectType])
    : null;
  // null \u2192 geriye d\u00f6n\u00fck uyum: filtre kapal\u0131 (projectType bilinmiyorsa eski davran\u0131\u015f).
  const isAllowed = (defName) => {
    if (!allowedKeys) return true;
    const key = DEF_NAME_TO_DOC_KEY[defName];
    if (!key) return true; // mapping'de olmayan (dinamik) isimler her zaman izinli
    return allowedKeys.has(key);
  };
  const baseDefs = INITIAL_DEFS
    .filter(def => def.name !== "Kat Plan\u0131")
    .filter(def => isAllowed(def.name));

  const dynamicFloorPlanDefs = floorPlans.map(plan => ({
    name: "Kat Plan\u0131",
    displayName: plan.name || "Kat Plan\u0131",
    w: plan.width + 2 * DOCUMENT_OFFSET,
    h: plan.height + 4 * DOCUMENT_OFFSET,
    hasFrame: true,
    floorPlanId: plan.id,
    floorType: plan.type,
    floorNumber: plan.number,
    floorCount: plan.floorCount || 1,
    objectsIds: plan.objectsIds,
    group: plan.group,
  }));

  let dynamicSitePlanDef = null;
  if (sitePlan?.width && sitePlan?.height && isAllowed("Vaziyet Plan\u0131")) {
    const maxH = MAIN_FRAME_HEIGHT - 2 * DOCUMENT_OFFSET;
    dynamicSitePlanDef = {
      name: "Vaziyet Plan\u0131", w: sitePlan.width, h: Math.min(sitePlan.height, maxH),
      hasFrame: true, sitePlanId: sitePlan.id, objectsIds: sitePlan.objectsIds, group: sitePlan.group,
    };
  }

  let groundFloorDef = null;
  if (floorPlans.length > 0) {
    // Topraklama kat planı floorPlans[0]'dan oluşturulur. Header'da Türkçe + kaynak
    // kat adı: "Topraklama Kat Planı - <kat>" (ör. "Topraklama Kat Planı - Bodrum Kat").
    // name (identifier) İngilizce "Ground Floor" kalır; header displayName'i kullanır.
    const gfFloorName = floorPlans[0].name;
    groundFloorDef = {
      name: "Ground Floor",
      displayName: gfFloorName ? `Topraklama Kat Planı - ${gfFloorName}` : 'Topraklama Kat Planı',
      w: floorPlans[0].width + 2 * DOCUMENT_OFFSET,
      h: floorPlans[0].height + 4 * DOCUMENT_OFFSET,
      hasFrame: true, floorPlanId: floorPlans[0].id,
      objectsIds: floorPlans[0].objectsIds, group: floorPlans[0].group,
    };
  }

  // Outlier filter
  floorPlans.forEach((plan) => {
    if (!plan.group) return;
    const planSize = Math.max(plan.width || 2000, plan.height || 1500);
    hideOutlierMeshes(plan.group, planSize, `plan/${plan.type}/${(plan.id || '').slice(-8)}`);
  });

  // YC OLE (Excel) mode'da mesh dimension'lar\u0131 def.w/def.h'ten gelir.
  // Eskiden w:125 idi \u2014 sync createYuklemeCetvel'in d\u00f6nd\u00fcrd\u00fc\u011f\u00fc 1060 ile
  // override oluyordu, ama OLE mode'da override yok \u2192 \u00e7ok dar mesh \u00e7\u0131k\u0131yordu
  // (kullan\u0131c\u0131: "10 kat k\u00fc\u00e7\u00fck"). Topraklama Hesab\u0131 w:600/h:1000 ile do\u011fru
  // \u00e7\u0131k\u0131yor; YC i\u00e7in yatay Excel layout'a g\u00f6re w:1060 ba\u015flang\u0131\u00e7 de\u011feri
  // (height cachedH ile OLE sonras\u0131 otomatik g\u00fcncellenir).
  const yuklemeCetveliDef = isAllowed("Y\u00fckleme Cetveli")
    ? { name: "Y\u00fckleme Cetveli", w: 1060, h: 1250, hasFrame: false }
    : null;
  const kolonSemaDef = isAllowed("Kolon \u015eemas\u0131")
    ? { name: "Kolon \u015eemas\u0131", w: 105, h: 500, hasFrame: false }
    : null;
  // Tek Hat \u015eemas\u0131 (single-line). createTekHatSema kendi width/height'\u0131n\u0131 d\u00f6ner
  // (buildSingleDocument override eder); w/h sadece ba\u015flang\u0131\u00e7 placeholder. \u0130sim
  // 'TekHat  \u015eemas\u0131' (\u00e7ift bo\u015fluk) createDocumentContent/prepareDocumentData/
  // DOC_NAME_TO_GROUP ile birebir e\u015fle\u015fir \u2014 bu def eksik oldu\u011fu i\u00e7in MULTI_METER/
  // SINGLE_UNIT/HIGH_RISE'da Tek Hat \u015eemas\u0131 hi\u00e7 olu\u015fturulmuyordu.
  // Tek Hat Şeması — MULTI_METER'da panel içeriğine göre grupla:
  // aynı branch circuit imzasına sahip paneller tek belgede gösterilir (NKT1-NKT3-NKT5 gibi).
  // Her grup ayrı bir def → ayrı bir doküman. Diğer proje tiplerinde tek def.
  let tekHatSemaDefs = [];
  if (isAllowed("TekHat  \u015eemas\u0131")) {
    if (projectType === PROJECT_TYPES.MULTI_METER && simpleSupply?.components?.length > 0) {
      const groups = groupPanelsByContent(simpleSupply, floorPlans);
      tekHatSemaDefs = groups.map(group => ({
        name: "TekHat  \u015eemas\u0131",
        w: 600, h: 600, hasFrame: false,
        tekHatGroupNames:            group.panelNames,
        tekHatRepresentativePanelId: group.representativePanelId,
      }));
    }
    if (tekHatSemaDefs.length === 0) {
      tekHatSemaDefs = [{ name: "TekHat  \u015eemas\u0131", w: 600, h: 600, hasFrame: false }];
    }
  }

  const topraklamaDetayIndex = baseDefs.findIndex(def => def.name === "Topraklama Detaylar\u0131");
  return [
    dynamicSitePlanDef,
    groundFloorDef,
    ...baseDefs.slice(0, topraklamaDetayIndex + 1),
    ...dynamicFloorPlanDefs,
    yuklemeCetveliDef,
    kolonSemaDef,
    ...tekHatSemaDefs,
    ...baseDefs.slice(topraklamaDetayIndex + 1),
  ].filter(Boolean);
}
